import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin } from 'lucide-react';

/**
 * Prominent Disclosure dialog shown BEFORE Android requests
 * ACCESS_BACKGROUND_LOCATION. Required by Google Play Console policy
 * for any app that uses location in the background.
 *
 * Once the user taps Continue we persist consent in localStorage so
 * the dialog only fires once per device.
 *
 * Visit any page with ?reset_bg_consent=1 to clear the persisted flag
 * (used to re-record the Play Console demo video).
 */

const CONSENT_KEY = 'rollm8s_bg_location_consent_v1';

export const hasBackgroundLocationConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch {
    return false;
  }
};

export const setBackgroundLocationConsent = (granted) => {
  try {
    if (granted) {
      localStorage.setItem(CONSENT_KEY, 'granted');
    } else {
      localStorage.removeItem(CONSENT_KEY);
    }
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

export default function BackgroundLocationDisclosureDialog({ open, onAllow, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent
        data-testid="bg-location-disclosure-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <MapPin className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center text-lg">
            Share live location with your ride
          </DialogTitle>
          <DialogDescription className="text-center pt-2 leading-relaxed">
            RollM8s collects location data to enable live ride-share tracking — even when you switch to another app, lock your screen, or take a phone call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm leading-relaxed">
          <p>
            <strong className="text-primary">Tap Continue</strong> if you're happy to allow location sharing in the background while your trip is active.
          </p>
          <p>
            <strong className="text-muted-foreground">Tap Cancel</strong> if you don't want to share your location when you come away from the app.
          </p>
          <p className="text-muted-foreground">
            Ending the trip terminates location sharing automatically — we never track you outside an active trip.
          </p>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="bg-location-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onAllow}
            data-testid="bg-location-allow-btn"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Follow-up dialog shown when the user has granted foreground location
 * but NOT "Allow all the time". On Android 11+ this second permission
 * can only be enabled via Settings — there is no runtime prompt. This
 * dialog explains that and opens the app's permission Settings page.
 */
export function BackgroundLocationSettingsPrompt({ open, onOpenSettings, onDismiss }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onDismiss?.()}>
      <DialogContent
        data-testid="bg-location-settings-prompt"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
            <MapPin className="h-7 w-7 text-amber-500" />
          </div>
          <DialogTitle className="text-center text-lg">
            One more step — enable in Settings
          </DialogTitle>
          <DialogDescription className="text-center pt-2 leading-relaxed">
            Android requires you to manually enable <strong>“Allow all the time”</strong> in Settings so the live map keeps streaming when you switch apps.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2 text-sm text-muted-foreground leading-relaxed">
          <p>Tap <strong>Open Settings</strong>, then under <strong>Location</strong> select <strong>“Allow all the time”</strong>. Come back here when done.</p>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onDismiss}
            data-testid="bg-location-settings-later-btn"
          >
            Maybe later
          </Button>
          <Button
            onClick={onOpenSettings}
            data-testid="bg-location-open-settings-btn"
          >
            Open Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
